import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  UserPlus, Eye, MessageSquare, StickyNote, Activity as ActivityIcon, ArrowLeft, ChevronUp, ChevronDown, Reply as ReplyIcon, X as XIcon
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import Composer from "./Composer";
import ActivityPanel from "./ActivityPanel";
import MyTagsEditor from "./MyTagsEditor";
import TeamTagsEditor from "./TeamTagsEditor";
import ForwardDialog from "./ForwardDialog";
import { Folder as FolderIcon, Forward as ForwardIcon } from "lucide-react";
import { toast } from "sonner";

function initialsOf(s) {
  return (s || "?").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
}

/**
 * Strip the empty paragraphs/br chains that Outlook & Word stuff between
 * the email body and signature. Operates on the HTML string before render.
 */
function tidyEmailHtml(html) {
  if (!html) return html;
  let s = html;
  // Drop Office's <o:p> namespace tags entirely
  s = s.replace(/<\/?o:p[^>]*>/gi, "");
  // Collapse <p>&nbsp;</p>, <p>  </p>, <p></p>, MsoNormal variants
  s = s.replace(/<p[^>]*>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, "");
  // Collapse <div>&nbsp;</div> blocks
  s = s.replace(/<div[^>]*>(?:\s|&nbsp;|<br\s*\/?>)*<\/div>/gi, "");
  // Collapse 3+ consecutive <br> into a single <br>
  s = s.replace(/(?:<br\s*\/?>\s*){3,}/gi, "<br>");
  return s;
}

export default function ThreadDetail({ threadId, currentUser, users, folders, statuses, myTagsList, teamTagsList, onChange, onMyTagsChange, onTeamTagsChange, onClose, onPrev, onNext, position }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [forwardSource, setForwardSource] = useState(null); // message being forwarded
  const [viewers, setViewers] = useState([]);
  const [tab, setTab] = useState("conversation");
  const [showReply, setShowReply] = useState(false);
  const viewerTimer = useRef();

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await api.get(`/threads/${threadId}`);
      setData(data);
    } catch (e) {
      setLoadError(e?.response?.data?.detail || e?.message || "Couldn't load this thread.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!threadId) return;
    setShowReply(false); // collapse the reply composer whenever the user opens a new thread
    load();
    // collision detection ping
    const ping = async () => {
      try {
        const { data } = await api.post(`/threads/${threadId}/view`, {});
        setViewers(data.viewers || []);
      } catch (e) { void e; }
    };
    ping();
    viewerTimer.current = setInterval(ping, 10000);
    return () => clearInterval(viewerTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  // Keyboard shortcut: R opens the reply composer (ignored while typing).
  useEffect(() => {
    if (!threadId) return;
    const onKey = (e) => {
      if (e.key !== "r" && e.key !== "R") return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (tab !== "conversation") return;
      setShowReply(true);
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [threadId, tab]);

  const refresh = async () => {
    await load();
    onChange?.();
  };

  const setStatus = async (status) => {
    await api.put(`/threads/${threadId}/status`, { status });
    await refresh();
  };

  const setAssignee = async (user_id) => {
    await api.put(`/threads/${threadId}/assign`, { user_id });
    await refresh();
  };

  if (!threadId) {
    return (
      <div className="flex-1 flex items-center justify-center bg-white" data-testid="thread-detail-empty">
        <div className="text-center max-w-sm px-6">
          <div className="w-12 h-12 mx-auto mb-3 bg-zinc-100 rounded-md flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-zinc-400" strokeWidth={1.5} />
          </div>
          <h3 className="font-heading text-base font-medium text-zinc-900 mb-1">Select a conversation</h3>
          <p className="text-sm text-zinc-500">Pick a thread from the list to read, reply, or assign.</p>
        </div>
      </div>
    );
  }

  if (loading || !data) {
    if (loadError) {
      return (
        <div className="flex-1 flex items-center justify-center bg-white px-6" data-testid="thread-detail-error">
          <div className="text-center max-w-sm">
            <div className="w-12 h-12 mx-auto mb-3 bg-red-50 border border-red-200 rounded-md flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-red-500" strokeWidth={1.5} />
            </div>
            <h3 className="font-heading text-base font-medium text-zinc-900 mb-1">Couldn't load this thread</h3>
            <p className="text-sm text-zinc-500 mb-4">{loadError}</p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={load}
                className="h-9 px-4 bg-[#0033CC] hover:bg-[#002299] text-white rounded-md text-sm font-medium"
                data-testid="thread-detail-retry"
              >
                Try again
              </button>
              {onClose && (
                <button
                  onClick={onClose}
                  className="h-9 px-4 border border-zinc-200 hover:bg-zinc-50 rounded-md text-sm font-medium text-zinc-700"
                  data-testid="thread-detail-back"
                >
                  Back to inbox
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }
    return <div className="flex-1 flex items-center justify-center text-sm text-zinc-500 bg-white" data-testid="thread-detail-loading">Loading thread…</div>;
  }

  const { thread, messages } = data;
  const replyMessages = messages.filter((m) => !m.is_internal_note);
  const noteMessages = messages.filter((m) => m.is_internal_note);
  const statusOpts = statuses && statuses.length > 0 ? statuses : [];
  const statusOpt = statusOpts.find((s) => s.id === thread.status) || { id: thread.status, label: thread.status, color: "#71717A" };

  return (
    <div className="flex-1 flex flex-col bg-white min-w-0 w-full safe-bottom thread-detail-shell" data-testid="thread-detail">
      {/* Header */}
      <div className="border-b border-zinc-200 shrink-0">
        {/* Row 1 — back + subject. Desktop adds actions as a sibling on the right. */}
        <div className="px-3 md:px-5 pt-2 md:pt-0 md:h-14 flex items-start md:items-center gap-2 md:gap-3">
          {onClose && (
            <button
              onClick={onClose}
              className="shrink-0 w-9 h-9 md:w-8 md:h-8 rounded-full hover:bg-zinc-100 text-zinc-600 hover:text-zinc-900 flex items-center justify-center transition-colors"
              title="Back to inbox (Esc)"
              data-testid="thread-close-button"
            >
              <ArrowLeft className="w-5 h-5 md:w-4 md:h-4" strokeWidth={2} />
            </button>
          )}
          {/* Prev/Next thread navigation — lets the user tick through the
              filtered thread list without going back to the split view.
              Buttons disable at the ends. Also driven by J/K keys. */}
          {(onPrev || onNext) && (
            <div className="hidden md:flex items-center border border-zinc-200 rounded-md overflow-hidden shrink-0" data-testid="thread-nav-group">
              <button
                onClick={onPrev}
                disabled={!onPrev}
                className="w-8 h-8 flex items-center justify-center text-zinc-600 hover:bg-zinc-100 disabled:text-zinc-300 disabled:cursor-not-allowed border-r border-zinc-200 transition-colors"
                title="Previous thread (K)"
                aria-label="Previous thread"
                data-testid="thread-prev-button"
              >
                <ChevronUp className="w-4 h-4" strokeWidth={2} />
              </button>
              <button
                onClick={onNext}
                disabled={!onNext}
                className="w-8 h-8 flex items-center justify-center text-zinc-600 hover:bg-zinc-100 disabled:text-zinc-300 disabled:cursor-not-allowed transition-colors"
                title="Next thread (J)"
                aria-label="Next thread"
                data-testid="thread-next-button"
              >
                <ChevronDown className="w-4 h-4" strokeWidth={2} />
              </button>
              {position && (
                <span
                  className="hidden lg:inline-block px-2 h-8 leading-8 text-[11px] text-zinc-500 font-mono border-l border-zinc-200"
                  aria-live="polite"
                  data-testid="thread-position"
                >
                  {position.index} of {position.total}
                </span>
              )}
            </div>
          )}
          <div className="min-w-0 flex-1 py-1 md:py-0">
            <h2 className="font-heading text-[15px] md:text-base font-medium tracking-tight leading-tight md:truncate line-clamp-2 md:line-clamp-none" data-testid="thread-subject">{thread.subject}</h2>
            <div className="flex items-center gap-1.5 text-[11px] md:text-xs text-zinc-500 mt-0.5 min-w-0">
              <span className="font-mono truncate min-w-0">{thread.participants?.[0]?.email}</span>
              {thread.company && <><span className="shrink-0">·</span><span className="shrink-0">{thread.company}</span></>}
              {thread.phone && <><span className="hidden md:inline">·</span><span className="hidden md:inline font-mono">{thread.phone}</span></>}
            </div>
          </div>
          {/* Desktop actions — sibling to title, kept on the same line. Hidden on mobile (mobile uses Row 2 below). */}
          <DesktopHeaderActions
            viewers={viewers}
            folders={folders}
            threadId={threadId}
            statusOpts={statusOpts}
            statusOpt={statusOpt}
            users={users}
            currentUser={currentUser}
            thread={thread}
            setStatus={setStatus}
            setAssignee={setAssignee}
            onChange={onChange}
          />
        </div>

        {/* Row 2 — actions. On mobile: horizontally scrollable strip. On desktop: same row as title (absolute-positioned right). */}
        <div className="md:hidden px-3 pb-2 pt-1 flex items-center gap-2 overflow-x-auto scrollbar-none" data-testid="thread-actions-mobile">
          {viewers.length > 0 && (
            <div className="shrink-0 flex items-center gap-1 px-2 h-7 bg-amber-50 border border-amber-200 rounded-full" data-testid="viewers-pill-mobile">
              <Eye className="w-3 h-3 text-amber-700" strokeWidth={1.5} />
              <span className="text-[11px] text-amber-800 truncate max-w-[120px]">{viewers.map((v) => v.user_name).join(", ")}</span>
            </div>
          )}
          {/* Status pill (the most-used action) */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="shrink-0 h-7 gap-1.5 border-zinc-200 rounded-full text-[11px] px-3" data-testid="status-dropdown-trigger-mobile">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusOpt?.color }} />
                {statusOpt?.label}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-44">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-zinc-500">Set stage</DropdownMenuLabel>
              {statusOpts.map((s) => (
                <DropdownMenuItem key={s.id} onClick={() => setStatus(s.id)} data-testid={`status-option-m-${s.id}`}>
                  <span className="w-2 h-2 rounded-full mr-2 shrink-0" style={{ background: s.color }} />
                  {s.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {/* Assign pill */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="shrink-0 h-7 gap-1.5 border-zinc-200 rounded-full text-[11px] px-3" data-testid="assign-dropdown-trigger-mobile">
                <UserPlus className="w-3 h-3" strokeWidth={1.5} />
                {thread.assignee_name || "Assign"}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-52">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-zinc-500">Assign to</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => setAssignee(currentUser.id)} data-testid="assign-to-me-m">
                <Avatar className="w-5 h-5 mr-2"><AvatarFallback className="text-[10px] bg-[#0033CC] text-white">{initialsOf(currentUser.name)}</AvatarFallback></Avatar>
                Me ({currentUser.name})
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {users.filter((u) => u.id !== currentUser.id && u.active).map((u) => (
                <DropdownMenuItem key={u.id} onClick={() => setAssignee(u.id)} data-testid={`assign-user-m-${u.id}`}>
                  <Avatar className="w-5 h-5 mr-2"><AvatarFallback className="text-[10px] bg-zinc-200">{initialsOf(u.name)}</AvatarFallback></Avatar>
                  {u.name}
                </DropdownMenuItem>
              ))}
              {thread.assigned_to && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setAssignee(null)} className="text-red-600" data-testid="unassign-m">
                    Unassign
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          {/* Folder pill */}
          {folders && folders.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="shrink-0 h-7 gap-1.5 border-zinc-200 rounded-full text-[11px] px-3" data-testid="folder-dropdown-trigger-mobile">
                  <FolderIcon className="w-3 h-3" strokeWidth={1.5} />
                  Folder
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-zinc-500">Add this thread to</DropdownMenuLabel>
                {folders.map((f) => {
                  const inFolder = (f.thread_ids || []).includes(threadId);
                  return (
                    <DropdownMenuItem
                      key={f.id}
                      onClick={async () => {
                        try {
                          if (inFolder) { await api.delete(`/folders/${f.id}/threads/${threadId}`); toast.success(`Removed from "${f.name}"`); }
                          else { await api.post(`/folders/${f.id}/threads`, { thread_id: threadId }); toast.success(`Added to "${f.name}"`); }
                          onChange?.();
                        } catch (e) { toast.error("Couldn't update folder"); }
                      }}
                      data-testid={`folder-toggle-m-${f.id}`}
                    >
                      <FolderIcon className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      <span className="flex-1">{f.name}</span>
                      {inFolder && <span className="text-xs text-emerald-600">✓</span>}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Desktop actions now live INSIDE Row 1 above (DesktopHeaderActions). The
            old absolute-positioned block has been removed. */}

        {/* Personal tags row */}
        <div className="px-5 pb-1 -mt-1">
          <MyTagsEditor
            threadId={threadId}
            initial={thread.my_tags || []}
            allTags={myTagsList}
            onChange={() => onMyTagsChange?.()}
          />
        </div>
        {/* Team tags row */}
        <div className="px-5 pb-2">
          <TeamTagsEditor
            threadId={threadId}
            initial={thread.team_tags || []}
            allTags={teamTagsList}
            onChange={() => onTeamTagsChange?.()}
          />
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col min-h-0">
        <TabsList className="rounded-none bg-transparent border-b border-zinc-200 h-10 px-3 justify-start shrink-0">
          <TabsTrigger value="conversation" className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#0033CC] rounded-none text-xs font-medium" data-testid="tab-conversation">
            <MessageSquare className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
            Conversation ({replyMessages.length})
          </TabsTrigger>
          <TabsTrigger value="notes" className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#0033CC] rounded-none text-xs font-medium" data-testid="tab-notes">
            <StickyNote className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
            Internal notes ({noteMessages.length})
          </TabsTrigger>
          <TabsTrigger value="activity" className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#0033CC] rounded-none text-xs font-medium" data-testid="tab-activity">
            <ActivityIcon className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
            Activity
          </TabsTrigger>
        </TabsList>

        <TabsContent value="conversation" className="flex-1 flex flex-col min-h-0 m-0 bg-[#F6F8FC] data-[state=inactive]:hidden">
          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="px-6 py-6 space-y-3">
              {replyMessages.map((m, idx) => {
                const isLast = idx === replyMessages.length - 1;
                return (
                  <details
                    key={m.id}
                    open
                    className="group border border-zinc-200 rounded-lg bg-white animate-fade-in shadow-sm overflow-hidden"
                    data-testid={`message-${m.id}`}
                  >
                    <summary className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-zinc-50 list-none [&::-webkit-details-marker]:hidden">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <Avatar className="w-8 h-8 shrink-0">
                          <AvatarFallback className={`text-xs font-medium ${m.direction === "outgoing" ? "bg-[#0033CC] text-white" : "bg-zinc-200 text-zinc-700"}`}>
                            {initialsOf(m.from_name || m.from_email)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{m.from_name || m.from_email}</p>
                          <p className="text-xs text-zinc-500 truncate">
                            {m.from_name ? <span className="font-mono">&lt;{m.from_email}&gt;</span> : null}
                            {!m.from_name && <span className="italic">to {(m.to_emails || []).slice(0, 1).join(", ") || "—"}</span>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0 ml-3">
                        <button
                          type="button"
                          onClick={(e) => {
                            // Prevent the <details> from toggling when the user
                            // clicks the Forward button in the summary row.
                            e.preventDefault();
                            e.stopPropagation();
                            setForwardSource(m);
                          }}
                          className="hidden sm:inline-flex items-center gap-1 h-7 px-2 rounded-md text-[11px] text-zinc-600 hover:text-[#0033CC] hover:bg-zinc-100 transition-colors"
                          title="Forward this message"
                          data-testid={`forward-button-${m.id}`}
                        >
                          <ForwardIcon className="w-3.5 h-3.5" strokeWidth={1.75} />
                          Forward
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setForwardSource(m); }}
                          className="sm:hidden w-7 h-7 rounded-md text-zinc-600 hover:text-[#0033CC] hover:bg-zinc-100 flex items-center justify-center"
                          title="Forward"
                          data-testid={`forward-button-m-${m.id}`}
                        >
                          <ForwardIcon className="w-3.5 h-3.5" strokeWidth={1.75} />
                        </button>
                        <span className="text-xs text-zinc-500 ml-1.5" title={m.sent_at}>{format(new Date(m.sent_at), "MMM d, h:mm a")}</span>
                      </div>
                    </summary>
                    <div className="px-5 py-4 border-t border-zinc-100 text-sm leading-relaxed text-zinc-800">
                      {m.body_html && m.body_html.length > 20 ? (
                        <div
                          className="email-body prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: tidyEmailHtml(m.body_html) }}
                        />
                      ) : (
                        <div className="whitespace-pre-wrap">{m.body_text}</div>
                      )}
                      {m.attachments && m.attachments.length > 0 && (
                        <div className="mt-4 pt-3 border-t border-zinc-100">
                          <p className="text-[10px] font-semibold tracking-[0.1em] uppercase text-zinc-500 mb-2">
                            {m.attachments.length} attachment{m.attachments.length === 1 ? "" : "s"}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {m.attachments.map((a, i) => {
                              const sizeKb = a.size ? Math.round(a.size / 1024) : null;
                              const ext = (a.filename || "").split(".").pop()?.toUpperCase().slice(0, 4) || "FILE";
                              return (
                                <a
                                  key={`${m.id}-${i}`}
                                  href={`${process.env.REACT_APP_BACKEND_URL}/api/messages/${m.id}/attachments/${a.gmail_attachment_id}`}
                                  className="flex items-center gap-2 border border-zinc-200 rounded-md px-2.5 py-1.5 text-xs hover:bg-zinc-50 transition-colors max-w-xs"
                                  data-testid={`attachment-${a.gmail_attachment_id}`}
                                  download={a.filename}
                                >
                                  <span className="inline-flex items-center justify-center w-7 h-7 bg-[#0033CC]/10 text-[#0033CC] rounded font-mono text-[10px] font-bold shrink-0">
                                    {ext}
                                  </span>
                                  <span className="flex flex-col min-w-0">
                                    <span className="truncate font-medium text-zinc-900">{a.filename}</span>
                                    {sizeKb && <span className="text-[10px] text-zinc-500">{sizeKb < 1024 ? `${sizeKb} KB` : `${(sizeKb / 1024).toFixed(1)} MB`}</span>}
                                  </span>
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </details>
                );
              })}
            </div>
          </div>
          {showReply ? (
            <div className="border-t border-zinc-200 bg-white shrink-0">
              <div className="px-4 pt-2 flex items-center justify-between">
                <span className="text-[11px] font-medium uppercase tracking-wide text-zinc-500">Reply</span>
                <button
                  type="button"
                  onClick={() => setShowReply(false)}
                  className="w-7 h-7 flex items-center justify-center rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
                  title="Close reply"
                  aria-label="Close reply"
                  data-testid="reply-close-button"
                >
                  <XIcon className="w-4 h-4" strokeWidth={1.75} />
                </button>
              </div>
              <Composer
                threadId={threadId}
                mode="reply"
                onSent={async () => { await refresh(); setShowReply(false); }}
                fromAddress={thread.inbox_email}
                defaultTo={(() => {
                  const lastIn = [...replyMessages].reverse().find((m) => m.direction === "incoming");
                  return lastIn?.from_email || thread.participants?.[0]?.email || "";
                })()}
              />
            </div>
          ) : (
            <div className="border-t border-zinc-200 bg-white px-4 py-3 shrink-0 flex items-center gap-2">
              <Button
                onClick={() => setShowReply(true)}
                className="h-9 gap-2 bg-[#0033CC] hover:bg-[#002299] text-white text-sm font-medium px-4"
                data-testid="reply-open-button"
              >
                <ReplyIcon className="w-4 h-4" strokeWidth={2} />
                Reply
              </Button>
              <span className="text-xs text-zinc-500 hidden sm:inline">or press <kbd className="px-1.5 py-0.5 border border-zinc-200 rounded text-[10px] font-mono bg-zinc-50">R</kbd> to reply</span>
            </div>
          )}
        </TabsContent>

        <TabsContent value="notes" className="flex-1 flex flex-col min-h-0 m-0 data-[state=inactive]:hidden">
          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="px-6 py-6 space-y-4">
              {noteMessages.length === 0 && (
                <div className="text-sm text-zinc-500 italic" data-testid="notes-empty">No internal notes yet. Add one below — your customer won't see it.</div>
              )}
              {noteMessages.map((m) => (
                <div key={m.id} className="border border-amber-200 rounded-md bg-amber-50/70 animate-fade-in" data-testid={`note-${m.id}`}>
                  <div className="px-4 py-2.5 border-b border-amber-200/60 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Avatar className="w-7 h-7"><AvatarFallback className="text-xs bg-amber-200 text-amber-900">{initialsOf(m.from_name)}</AvatarFallback></Avatar>
                      <div>
                        <p className="text-sm font-medium">{m.from_name}</p>
                        <p className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold">Internal note</p>
                      </div>
                    </div>
                    <span className="text-xs text-amber-700">{formatDistanceToNow(new Date(m.sent_at), { addSuffix: true })}</span>
                  </div>
                  <div className="px-4 py-4 text-sm leading-relaxed text-zinc-800 whitespace-pre-wrap">{m.body_text}</div>
                </div>
              ))}
            </div>
          </div>
          <Composer threadId={threadId} mode="note" onSent={refresh} />
        </TabsContent>

        <TabsContent value="activity" className="flex-1 m-0 min-h-0 overflow-hidden data-[state=inactive]:hidden">
          <ActivityPanel threadId={threadId} />
        </TabsContent>
      </Tabs>

      <ForwardDialog
        open={!!forwardSource}
        onOpenChange={(v) => { if (!v) setForwardSource(null); }}
        message={forwardSource}
        onSent={refresh}
      />
    </div>
  );
}

// Desktop-only header action group — sibling to the title row.
// Hidden below md: so the mobile pill strip (in-page) handles small screens.
function DesktopHeaderActions({ viewers, folders, threadId, statusOpts, statusOpt, users, currentUser, thread, setStatus, setAssignee, onChange }) {
  return (
    <div className="hidden md:flex items-center gap-1.5 shrink-0">
      {viewers.length > 0 && (
        <div className="flex items-center gap-1 px-2 py-1 bg-amber-50 border border-amber-200 rounded-md mr-1" data-testid="viewers-pill">
          <Eye className="w-3.5 h-3.5 text-amber-700" strokeWidth={1.5} />
          <span className="text-xs text-amber-800">{viewers.map((v) => v.user_name).join(", ")} viewing</span>
        </div>
      )}
      {folders && folders.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 gap-1.5 border-zinc-200" data-testid="folder-dropdown-trigger">
              <FolderIcon className="w-3.5 h-3.5" strokeWidth={1.5} />
              Add to folder
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-zinc-500">Add this thread to</DropdownMenuLabel>
            {folders.map((f) => {
              const inFolder = (f.thread_ids || []).includes(threadId);
              return (
                <DropdownMenuItem
                  key={f.id}
                  onClick={async () => {
                    try {
                      if (inFolder) { await api.delete(`/folders/${f.id}/threads/${threadId}`); toast.success(`Removed from "${f.name}"`); }
                      else { await api.post(`/folders/${f.id}/threads`, { thread_id: threadId }); toast.success(`Added to "${f.name}"`); }
                      onChange?.();
                    } catch (e) { toast.error("Couldn't update folder"); }
                  }}
                  data-testid={`folder-toggle-${f.id}`}
                >
                  <FolderIcon className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  <span className="flex-1">{f.name}</span>
                  {inFolder && <span className="text-xs text-emerald-600">✓</span>}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 gap-1.5 border-zinc-200" data-testid="status-dropdown-trigger">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusOpt?.color }} />
            {statusOpt?.label}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-zinc-500">Set stage</DropdownMenuLabel>
          {statusOpts.map((s) => (
            <DropdownMenuItem key={s.id} onClick={() => setStatus(s.id)} data-testid={`status-option-${s.id}`}>
              <span className="w-2 h-2 rounded-full mr-2 shrink-0" style={{ background: s.color }} />
              {s.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 gap-1.5 border-zinc-200" data-testid="assign-dropdown-trigger">
            <UserPlus className="w-3.5 h-3.5" strokeWidth={1.5} />
            {thread.assignee_name || "Assign"}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-zinc-500">Assign to</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => setAssignee(currentUser.id)} data-testid="assign-to-me">
            <Avatar className="w-5 h-5 mr-2"><AvatarFallback className="text-[10px] bg-[#0033CC] text-white">{initialsOf(currentUser.name)}</AvatarFallback></Avatar>
            Me ({currentUser.name})
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {users.filter((u) => u.id !== currentUser.id && u.active).map((u) => (
            <DropdownMenuItem key={u.id} onClick={() => setAssignee(u.id)} data-testid={`assign-user-${u.id}`}>
              <Avatar className="w-5 h-5 mr-2"><AvatarFallback className="text-[10px] bg-zinc-200">{initialsOf(u.name)}</AvatarFallback></Avatar>
              {u.name}
              <span className="ml-auto text-[10px] text-zinc-500 uppercase">{u.role}</span>
            </DropdownMenuItem>
          ))}
          {thread.assigned_to && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setAssignee(null)} className="text-red-600" data-testid="unassign">
                Unassign
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
